import { useState } from "react";

import { useUploadFiles } from "../../../api/hooks/fileStorage/useUploadFiles";
import { useCreateIdpFdDocument } from "../../../api/hooks/idpFd/useCreateIdpFdDocument";
import { DocumentType } from "../types";
import { mapDocumentTypeToDocType } from "../utils";

export const useDocumentProcessing = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [createdIdpId, setCreatedIdpId] = useState<number | null>(null);

  const [, uploadFiles] = useUploadFiles();
  const [, createIdpFdDocument] = useCreateIdpFdDocument();

  const processDocument = async (
    file: File,
    documentType: DocumentType,
    customerId: number
  ): Promise<{ success: boolean; documentId?: number; error?: string }> => {
    setLoading(true);

    try {
      const uploadResult = await uploadFiles({
        input: [file],
      });

      if (uploadResult.error || !uploadResult.data) {
        throw new Error("Failed to upload file");
      }

      const uploadedFile = uploadResult.data.uploadFiles[0];
      const documentPath = uploadedFile.filePath;

      const mappedDocType = mapDocumentTypeToDocType(documentType);

      const createIdpResult = await createIdpFdDocument({
        input: {
          customerId,
          document: documentPath,
          documentType: mappedDocType,
        },
      });

      if (createIdpResult.error || !createIdpResult.data) {
        throw new Error("Failed to create IDP document");
      }

      const createdIdp = createIdpResult.data.createIdpFd;
      setCreatedIdpId(createdIdp.id);
      setLoading(false);

      return {
        success: true,
        documentId: createdIdp.id,
      };
    } catch (error) {
      console.error("Error processing document:", error);
      setLoading(false);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const submitDocument = (): void => {
    if (createdIdpId) {
      console.log("Document submitted successfully! IDP ID:", createdIdpId);
    } else {
      console.error("No IDP document ID available for submission");
    }
  };

  return {
    loading,
    processDocument,
    submitDocument,
    createdIdpId,
  };
};
